/**
 * Bridge to the native ITM Android app for saving files to the device.
 *
 * The Android app renders this PWA inside a WebView. A WebView cannot act on
 * the blob: URL downloads produced by file-saver's saveAs(): the blob only
 * exists inside this page's renderer process, and Android hands downloads to
 * the host app, which has no way to dereference a blob: URL (DownloadManager
 * rejects non-http(s) schemes outright). The app therefore injects a
 * `window.ITMDownload` object (ItmDownloadBridge.kt in the ITM Mobile repo)
 * and we hand it the file's bytes directly as base64 chunks; the app streams
 * them to a file and offers the user an open/share sheet.
 *
 * Bridge contract (all methods return synchronously on the app side):
 *   beginDownload(fileName, mimeType, totalSize) -> sessionId (0 = refused)
 *   appendChunk(sessionId, base64Chunk)          -> true to continue sending;
 *                                                   false = session discarded
 *   endDownload(sessionId)                       -> true if fully saved
 *   abortDownload(sessionId, reason)             -> void
 */

/**
 * Multiple of 3 so every chunk encodes to base64 without '=' padding; the app
 * can then decode each chunk independently and stream the bytes to disk.
 * Keeps peak memory per bridge call at ~700 KiB regardless of file size.
 */
const CHUNK_SIZE = 524286;

export const isNativeDownloadBridgeAvailable = () =>
  typeof window !== 'undefined' &&
  !!window.ITMDownload &&
  typeof window.ITMDownload.beginDownload === 'function' &&
  typeof window.ITMDownload.appendChunk === 'function' &&
  typeof window.ITMDownload.endDownload === 'function' &&
  typeof window.ITMDownload.abortDownload === 'function';

/**
 * True when running inside an Android WebView (e.g. the ITM app wrapper).
 * Android System WebView carries a '; wv)' token in its user agent string;
 * regular Chrome and Custom Tabs do not.
 */
export const isAndroidAppWebView = () =>
  typeof navigator !== 'undefined' && /; wv\)/.test(navigator.userAgent);

const readChunkAsBase64 = (chunk) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      resolve(dataUrl.substring(dataUrl.indexOf(',') + 1));
    };
    reader.onerror = () =>
      reject(reader.error || new Error('FileReader failed reading blob chunk'));
    reader.readAsDataURL(chunk);
  });

/**
 * Stream a Blob to the native app. Resolves true when the app confirms the
 * file was fully written and handed to the user; false when the app refused
 * the transfer or it failed part way through.
 *
 * @param {Blob} blob the file contents
 * @param {string} fileName suggested file name, including its extension
 * @returns {Promise<boolean>}
 */
export const saveBlobViaNativeBridge = async (blob, fileName) => {
  if (!isNativeDownloadBridgeAvailable()) {
    return false;
  }

  const bridge = window.ITMDownload;
  let sessionId = 0;

  try {
    sessionId = bridge.beginDownload(
      fileName,
      blob.type || 'application/octet-stream',
      blob.size,
    );

    if (!sessionId) {
      return false;
    }

    for (let offset = 0; offset < blob.size; offset += CHUNK_SIZE) {
      const chunk = blob.slice(offset, Math.min(offset + CHUNK_SIZE, blob.size));
      const base64 = await readChunkAsBase64(chunk);

      if (!bridge.appendChunk(sessionId, base64)) {
        // The app has already discarded the session; no abort needed.
        return false;
      }
    }

    return bridge.endDownload(sessionId) === true;
  } catch (ex) {
    console.debug('Native download bridge transfer failed', ex);

    if (sessionId) {
      try {
        bridge.abortDownload(sessionId, String(ex));
      } catch (abortEx) {
        console.debug('Native download bridge abort failed', abortEx);
      }
    }

    return false;
  }
};
